import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const HeroCommand = styled.section`
  text-align: center;
  padding: 3rem 0 2.5rem;
`;

const HeroGreeting = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const HeroSubtitle = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const CommandBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
`;

const CommandInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CmdIcon = styled.span`
  position: absolute;
  left: 1.25rem;
  color: var(--accent);
  font-size: 1.25rem;
  pointer-events: none;
`;

const CommandInput = styled.input`
  width: 100%;
  padding: 1.125rem 1.25rem 1.125rem 3.25rem;
  background: var(--cmd-bg);
  border: 2px solid var(--border);
  border-radius: 16px;
  font-family: inherit;
  font-size: 1.0625rem;
  color: var(--text-primary);
  transition: all 0.3s;
  outline: none;
  box-shadow: var(--shadow-lg);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 4px var(--ring), var(--shadow-xl);
  }
`;

const QuickActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const QuickAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
`;

const SectionLink = styled.a`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;
`;

const StatsRow = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
`;

const StatChange = styled.span<{ $positive?: boolean }>`
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  background: ${({ $positive }) =>
    $positive ? 'var(--success-subtle)' : 'var(--error-subtle)'};
  color: ${({ $positive }) =>
    $positive ? 'var(--success)' : 'var(--error)'};
`;

const Sparkline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 28px;
  margin-top: 0.75rem;
`;

const SparkBar = styled.div<{ $h: number; $color?: string }>`
  flex: 1;
  border-radius: 2px;
  background: ${({ $color }) => $color || 'var(--accent)'};
  opacity: 0.6;
  height: ${({ $h }) => $h}%;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const TwoCol = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const InsightItem = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const InsightIcon = styled.div<{ $bg?: string; $color?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg || 'var(--accent-subtle)'};
  color: ${({ $color }) => $color || 'var(--accent)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const InsightText = styled.div`
  flex: 1;
`;

const InsightTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.125rem;
`;

const InsightDesc = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const ActivityDot = styled.div<{ $color?: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color || 'var(--accent)'};
  flex-shrink: 0;
`;

const ActivityText = styled.div`
  flex: 1;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const ActivityTime = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  white-space: nowrap;
`;

/* ---------- component ---------- */
const CommandCenter: React.FC = () => {
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <PageBody className="fade-in">
      <Container>
        <HeroCommand>
          <HeroGreeting>{getGreeting()}</HeroGreeting>
          <HeroSubtitle>
            What would you like to do with your agents today?
          </HeroSubtitle>

          <CommandBar>
            <CommandInputWrap>
              <CmdIcon>&#x2728;</CmdIcon>
              <CommandInput
                type="text"
                placeholder='Ask Lumina anything... "Show failed sessions from yesterday"'
              />
            </CommandInputWrap>
          </CommandBar>

          <QuickActions>
            <QuickAction onClick={() => navigate('/agents')}>
              <span>&#x2795;</span> New Agent
            </QuickAction>
            <QuickAction>
              <span>&#x1F9EA;</span> Test Agent
            </QuickAction>
            <QuickAction>
              <span>&#x1F680;</span> Publish
            </QuickAction>
            <QuickAction onClick={() => navigate('/sessions')}>
              <span>&#x1F4CB;</span> Browse Sessions
            </QuickAction>
            <QuickAction onClick={() => navigate('/analytics')}>
              <span>&#x1F4CA;</span> View Analytics
            </QuickAction>
          </QuickActions>
        </HeroCommand>

        <SectionHeader>
          <SectionTitle>Platform Overview</SectionTitle>
          <SectionLink onClick={() => navigate('/analytics')}>
            View analytics &rarr;
          </SectionLink>
        </SectionHeader>

        <StatsRow>
          <StatCard>
            <StatLabel>Active Agents</StatLabel>
            <StatValue>
              12
              <StatChange $positive>+2</StatChange>
            </StatValue>
            <Sparkline>
              {[40, 55, 35, 70, 60, 80, 75].map((h, i) => (
                <SparkBar key={i} $h={h} />
              ))}
            </Sparkline>
          </StatCard>
          <StatCard>
            <StatLabel>Total Sessions</StatLabel>
            <StatValue>
              1,847
              <StatChange $positive>+12%</StatChange>
            </StatValue>
            <Sparkline>
              {[30, 50, 45, 55, 70, 65, 80].map((h, i) => (
                <SparkBar key={i} $h={h} $color="var(--info)" />
              ))}
            </Sparkline>
          </StatCard>
          <StatCard>
            <StatLabel>Avg Response</StatLabel>
            <StatValue>
              1.2s
              <StatChange $positive>-8%</StatChange>
            </StatValue>
            <Sparkline>
              {[80, 70, 65, 55, 50, 45, 40].map((h, i) => (
                <SparkBar key={i} $h={h} $color="var(--success)" />
              ))}
            </Sparkline>
          </StatCard>
          <StatCard>
            <StatLabel>Success Rate</StatLabel>
            <StatValue>
              94.2%
              <StatChange $positive>+1.3%</StatChange>
            </StatValue>
            <Sparkline>
              {[85, 88, 90, 87, 92, 91, 94].map((h, i) => (
                <SparkBar key={i} $h={h} $color="var(--warning)" />
              ))}
            </Sparkline>
          </StatCard>
        </StatsRow>

        <TwoCol>
          <Panel>
            <SectionHeader>
              <SectionTitle>&#x2728; AI Insights</SectionTitle>
            </SectionHeader>
            <InsightItem>
              <InsightIcon $bg="var(--warning-subtle)" $color="var(--warning)">
                &#x26A0;
              </InsightIcon>
              <InsightText>
                <InsightTitle>
                  Emily-Reservations timeout rate increased
                </InsightTitle>
                <InsightDesc>
                  3 timeouts in the last hour. External API latency may be
                  elevated.
                </InsightDesc>
              </InsightText>
            </InsightItem>
            <InsightItem>
              <InsightIcon
                $bg="var(--success-subtle)"
                $color="var(--success)"
              >
                &#x2713;
              </InsightIcon>
              <InsightText>
                <InsightTitle>Emily&apos;s tone update improved CSAT</InsightTitle>
                <InsightDesc>
                  Formal tone change led to +8% satisfaction in the last 24h.
                </InsightDesc>
              </InsightText>
            </InsightItem>
            <InsightItem>
              <InsightIcon $bg="var(--info-subtle)" $color="var(--info)">
                &#x1F4A1;
              </InsightIcon>
              <InsightText>
                <InsightTitle>Consider adding a catering agent</InsightTitle>
                <InsightDesc>
                  12 unresolved queries about catering in the past week.
                </InsightDesc>
              </InsightText>
            </InsightItem>
          </Panel>

          <Panel>
            <SectionHeader>
              <SectionTitle>Recent Activity</SectionTitle>
              <SectionLink onClick={() => navigate('/sessions')}>
                View all &rarr;
              </SectionLink>
            </SectionHeader>
            <ActivityItem>
              <ActivityDot $color="var(--success)" />
              <ActivityText>
                <strong>Emily</strong> completed reservation for user-3847
              </ActivityText>
              <ActivityTime>2m ago</ActivityTime>
            </ActivityItem>
            <ActivityItem>
              <ActivityDot $color="var(--error)" />
              <ActivityText>
                <strong>Emily-Reservations</strong> task timed out
              </ActivityText>
              <ActivityTime>15m ago</ActivityTime>
            </ActivityItem>
            <ActivityItem>
              <ActivityDot $color="var(--success)" />
              <ActivityText>
                <strong>Emily-Menu</strong> answered gluten-free query
              </ActivityText>
              <ActivityTime>42m ago</ActivityTime>
            </ActivityItem>
            <ActivityItem>
              <ActivityDot $color="var(--info)" />
              <ActivityText>
                <strong>Emily</strong> config updated — formal tone applied
              </ActivityText>
              <ActivityTime>1h ago</ActivityTime>
            </ActivityItem>
            <ActivityItem>
              <ActivityDot $color="var(--success)" />
              <ActivityText>
                <strong>Martin</strong> handled remote work policy query
              </ActivityText>
              <ActivityTime>1h ago</ActivityTime>
            </ActivityItem>
          </Panel>
        </TwoCol>
      </Container>
    </PageBody>
  );
};

export default CommandCenter;
